import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { createImageVariantConfig, types as sdkTypes } from '../../util/sdkLoader';
import { getFavoriteListingIds, normalizeFavoriteListingIds } from '../../util/favorites';

const { UUID } = sdkTypes;

const queryFavoriteListingsPayloadCreator = (
  { listingIds = [], config },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const normalizedIds = normalizeFavoriteListingIds(listingIds);
  if (normalizedIds.length === 0) {
    return Promise.resolve({ listingRefs: [], missingIds: [] });
  }

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const queryParams = {
    ids: normalizedIds.join(','),
    include: ['author', 'images'],
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'deleted',
      'state',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.cardStyle',
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
      'publicData.priceVariationsEnabled',
      'publicData.priceVariants',
      'publicData.buyNowPrice',
      'publicData.reservePrice',
      'publicData.isAuction',
      'publicData.auctionEndUnix',
      'publicData.auctionEndTime',
      'publicData.producer',
      'publicData.fillDate',
      'publicData.mashbill',
      'publicData.moq',
      'metadata.userBids',
    ],
    'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  };

  return sdk.listings
    .query(queryParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      const listings = response.data.data || [];
      const foundIds = new Set(listings.map(l => l.id.uuid));
      const listingRefs = normalizedIds
        .filter(id => foundIds.has(id))
        .map(id => ({ id: new UUID(id), type: 'listing' }));
      const missingIds = normalizedIds.filter(id => !foundIds.has(id));
      return { listingRefs, missingIds };
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const queryFavoriteListingsThunk = createAsyncThunk(
  'FavoritesPage/queryFavoriteListings',
  queryFavoriteListingsPayloadCreator
);

export const queryFavoriteListings = (listingIds, config) => dispatch => {
  return dispatch(queryFavoriteListingsThunk({ listingIds, config })).unwrap();
};

const initialState = {
  favoriteListingRefs: [],
  queryInProgress: false,
  queryError: null,
};

const favoritesPageSlice = createSlice({
  name: 'FavoritesPage',
  initialState,
  reducers: {
    resetFavoritesPage: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(queryFavoriteListingsThunk.pending, state => {
        state.queryInProgress = true;
        state.queryError = null;
      })
      .addCase(queryFavoriteListingsThunk.fulfilled, (state, action) => {
        state.queryInProgress = false;
        state.favoriteListingRefs = action.payload.listingRefs;
      })
      .addCase(queryFavoriteListingsThunk.rejected, (state, action) => {
        state.queryInProgress = false;
        state.queryError = action.payload;
        state.favoriteListingRefs = [];
      });
  },
});

export default favoritesPageSlice.reducer;
export const { resetFavoritesPage } = favoritesPageSlice.actions;

export const loadData = (params, search, config) => (dispatch, getState) => {
  const currentUser = getState().user?.currentUser;
  const favoriteIds = getFavoriteListingIds(currentUser);
  if (!currentUser || favoriteIds.length === 0) {
    return Promise.resolve();
  }
  return dispatch(queryFavoriteListings(favoriteIds, config));
};
